/**
 * LineStabilityIndicator - Shows even vs odd line count status for song sections
 *
 * In songwriting, sections with even line counts are considered more stable
 * and balanced (better for verses/choruses). This indicator provides quick
 * visual feedback on section structure.
 *
 * Visual design:
 * - Even lines: Green dot (#10b981)
 * - Odd lines: Gray dot (#9ca3af)
 * - Hover: Tooltip showing line count and parity
 * - Edge case: 0 lines treated as even (mathematically correct)
 */

import { useState } from 'react'

interface LineStabilityIndicatorProps {
  lineCount: number
}

/**
 * LineStabilityIndicator component
 *
 * Renders a small colored dot indicating whether a section has an even or odd
 * number of lines. Includes an accessible tooltip on hover.
 */
export function LineStabilityIndicator({ lineCount }: LineStabilityIndicatorProps) {
  const [showTooltip, setShowTooltip] = useState(false)

  // Edge case: 0 lines is mathematically even
  const isEven = lineCount % 2 === 0
  const parity = isEven ? 'Even' : 'Odd'
  const tooltipText = `${parity}: ${lineCount} ${lineCount === 1 ? 'line' : 'lines'}`

  return (
    <div
      data-testid="line-stability-indicator"
      className={`
        line-stability-indicator
        ${isEven ? 'line-stability-indicator--even' : 'line-stability-indicator--odd'}
      `}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      aria-label={tooltipText}
      role="status"
      title={tooltipText}
    >
      {showTooltip && (
        <div className="line-stability-tooltip">
          {tooltipText}
        </div>
      )}
    </div>
  )
}
